import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    ValidationResult,
    FormValidationSummary,
    FormTextField,
    FormDatePicker,
    FormDropdown,
    mapToDropdownOption,
    removeTextQuotes,
    TextField
} from "sprint-common-web";

import { FileRecordLibrary } from "../../../../../../../common/api/master-api/file-records/details/GetFileRecordDetailsResponse";
import { OriginatorsDataContext } from "../../../../../../../common/data/lookup-data-providers/OriginatorsDataProvider";
import { StorageType } from "../../../../../../../common/types/StorageType";
import { validateFileExtension } from "../../../../../../../common/utils/validateFileExtension";
import { filePathValidationRulePattern } from "../../../../../../../common/utils/validation-rules/filePath";
import { useFileRecordDetailsState } from "../../../../hooks/useFileRecordDetailsState";

export interface LibraryFormProps {
    isLoading: boolean;
    onCancel?(): void;
    validationResult?: ValidationResult;
    onSubmit(library: FileRecordLibrary): void;
}

interface LibraryInfoFormInputs {
    fileName: string;
    arrivalDate: Date;
    originator: string;
    reference: string;
    path: string;
}

export const LibraryForm = (props: LibraryFormProps): JSX.Element => {
    const { fileRecordDetails } = useFileRecordDetailsState();
    const { originators } = useContext(OriginatorsDataContext);
    const [storageType, setStorageType] = React.useState<StorageType>(
        fileRecordDetails.storageType ?? StorageType.Cloud
    );

    const { handleSubmit, errors, control } = useForm<LibraryInfoFormInputs>({
        defaultValues: {
            fileName: fileRecordDetails.deliveryFileName,
            arrivalDate: fileRecordDetails.arrivalDate,
            originator: fileRecordDetails.originator?.id,
            reference: fileRecordDetails.reference,
            path: fileRecordDetails.deliveryPath
        }
    });

    const closeClearedAndCallOnClose = () => {
        if (props.onCancel) {
            props.onCancel();
        }
    };

    const callOnSubmit = (formInputs: LibraryInfoFormInputs) => {
        const originator = originators.find((o) => o.id === formInputs.originator);

        if (!originator) {
            throw new Error("Supplied originator does not exist");
        }

        props.onSubmit({
            id: fileRecordDetails.deliveryId,
            arrivalDate: formInputs.arrivalDate.toISOString(),
            fileName: formInputs.fileName,
            originator,
            storageType,
            path: formInputs.path,
            reference: formInputs.reference
        });
    };

    return (
        <Form onSubmit={handleSubmit(callOnSubmit)}>
            <FormFields>
                <FormTextField
                    label="Delivery file name"
                    name="fileName"
                    control={control}
                    errors={errors}
                    rules={{
                        required: "Enter delivery file name",
                        validate: (filename) => validateFileExtension(filename, fileRecordDetails.type)
                    }}
                />
                <FormDatePicker
                    label="Arrival date"
                    name="arrivalDate"
                    control={control}
                    errors={errors}
                    rules={{
                        required: "Enter arrival date"
                    }}
                />
                <FormDropdown
                    label={"Originator"}
                    name={"originator"}
                    control={control}
                    errors={errors}
                    options={originators.map((originator) => mapToDropdownOption(originator))}
                    rules={{
                        required: "Select originator"
                    }}
                />
                <FormTextField label="Reference" name="reference" control={control} errors={errors} />
                <FormTextField
                    label="File path"
                    name="path"
                    control={control}
                    errors={errors}
                    onInputChange={(value) => {
                        const filePath = removeTextQuotes(value);
                        control.setValue("path", filePath);
                        setStorageType(!filePath ? StorageType.Cloud : StorageType.OnPrem);
                    }}
                    rules={{
                        pattern: filePathValidationRulePattern
                    }}
                />
                <TextField
                    disabled
                    label="Storage type"
                    name={"storageType"}
                    value={storageType === StorageType.Cloud ? "Cloud" : "OnPrem"}
                />
                {props.validationResult && <FormValidationSummary validationResult={props.validationResult} />}
                <FormActions onCancel={closeClearedAndCallOnClose} isLoading={props.isLoading} />
            </FormFields>
        </Form>
    );
};
